import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    EventModel,
    EventOption,
    EventOptionGroup,
    EventParticipantImage,
    ExtendedDisplayType,
    Tv2MarketParameters,
} from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { NativePriceFormatterPipe } from '@frontend/sports/odds/feature/native-price';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { ParticipantImageComponent, ParticipantImageService, ParticipantType, isPlayerMatched } from '@frontend/sports/participant-image/feature';
import { NOT_APPLICABLE, TrackingService, TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Store } from '@ngrx/store';
import { RxFor } from '@rx-angular/template/for';
import { ceil } from 'lodash-es';
import { Subscription, map } from 'rxjs';

import { ISheetViewState, sheetViewStateSelector } from '../sheet-view-core/sheet-view.state';
import { SHEET_VIEW_OPEN } from '../sheet-view-core/sheet-view.token';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupRendererDirective } from './option-group-renderer.directive';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { loadPlayerStatisticsData } from './player-statistics/player-statistics.actions';
import { PlayerStatisticsComponent } from './player-statistics/player-statistics.component';
import { PlayerStatisticsStoreData } from './player-statistics/player-statistics.model';
import { PlayerStatisticsService } from './player-statistics/player-statistics.service';
import { selectDataByEventAndMarket } from './player-statistics/player-statistics.state';
import { PlayerStatsComponent } from './player-stats/player-stats.component';

@Component({
    selector: 'ms-regular-option-group',
    templateUrl: './regular-option-group.component.html',
    styleUrl: './regular-option-group.component.scss',
    providers: [ParticipantImageService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MarketHelpComponent,
        OptionGroupRendererDirective,
        RxFor,
        OptionComponent,
        OptionPanelBottomActionComponent,
        IconCustomComponent,
        ParticipantImageComponent,
        PlayerStatsComponent,
        NativePriceFormatterPipe,
        NgTemplateOutlet,
        PlayerStatisticsComponent,
        IconCustomComponent,
    ],
})
export class RegularOptionGroupComponent implements OnInit, OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';

    optionGroup = signal<EventOptionGroup>(null!);
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input({ alias: 'optionGroup', required: true })
    set _optionGroup(optionGroup: EventOptionGroup) {
        this.optionGroup.set(optionGroup);
    }

    @Input() thresholds?: OptionPanelThreshold[];
    @Input() showMarketHelp: boolean;
    @Input() crmPromotedOptionIdsMap: { [optionId: number]: number } = {};
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Input() calendarTrack?: string;
    @Input() groupType: OptionGroupType;
    @Input() showPlayerStats = false;

    @Output() expandClicked = new EventEmitter<boolean>();
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    threshold = signal(0);
    more = signal(false);
    isExpanded = signal(false);
    isEpcotEnabled = false;
    isUSGroupedListEnabled = false;
    trackingSource: TrackingSource;
    participantType = ParticipantType;

    useMsOptionRenderer = false;
    optionImages: NumberDictionary<EventParticipantImage> = {};
    isShowOptionImage = false;
    visibleOptions = computed(() => {
        if (this.isInSheetView || this.isExpanded() || !this.threshold()) {
            return this.optionGroup().options;
        }

        return this.optionGroup().options.slice(0, this.threshold());
    });

    columns = signal(1);

    visibleRows = computed(() => Math.floor(this.visibleOptions().length / this.columns()));

    visibleOptions$ = toObservable(this.visibleOptions);

    columnsDetermined = false;

    showMoreData = computed(() => {
        const optionCount = this.optionGroup().options.length;

        return {
            totalCount: optionCount,
            initialVisibleCount: this.threshold() || optionCount,
            pageSize: optionCount,
        };
    });

    isShowHeadshotImage = false;
    isPlayerStatisticsEnabled = false;
    playerStatistics = signal<PlayerStatisticsStoreData>({
        isLoading: true,
        isError: false,
        data: undefined,
    });

    store = inject(Store);
    private destroyRef = inject(DestroyRef);
    playerStatisticsService = inject(PlayerStatisticsService);
    playerStatisticsSubscription: Subscription;

    isStatisticsOpen = computed(() => {
        const statisticsOpenState: { [key: string]: boolean } = {};
        this.optionGroup().options.forEach((option: EventOption) => {
            const key = `${option.optionGroupId}_${option.playerId}`;
            statisticsOpenState[key] = !!this.playerStatisticsService.getPlayerStatistics(
                this.playerStatistics()?.data,
                option.optionGroupId?.toString(),
                option.name,
            )?.isOpen;
        });
        return statisticsOpenState;
    });
    readonly playerStatisticsMessages = this.playerStatisticsService.playerStatisticsMessages;

    isSheetViewOpen = toSignal(this.store.select(sheetViewStateSelector).pipe(map((sheetViewState: ISheetViewState) => sheetViewState.isOpen)), {
        initialValue: false,
    });

    constructor(
        public sitecore: Sitecore,
        private trackingService: TrackingService,
        private epcotConfig: EpcotConfig,
        private marketGroupingConfig: MarketGroupingConfig,
        private participantImageService: ParticipantImageService,
    ) {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    ngOnInit() {
        this.isUSGroupedListEnabled = this.groupType === OptionGroupType.USGroupedList;
        this.useMsOptionRenderer = !this.isEpcotEnabled && !this.isUSGroupedListEnabled;
        if (this.isUSGroupedListEnabled) {
            this.columns.set(2);
        }
        if (!this.useMsOptionRenderer) {
            this.threshold.set(this.calcThreshold(this.isUSGroupedListEnabled ? 2 : 1));
        }
        this.trackingSource = { source: this.tracking, calendarTitle: this.calendarTrack, moduleType: this.trackingModuleType };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sourceOfPick || changes.event) {
            this.isShowOptionImage = this.participantImageService.showOptionsImage(this.event, this.optionGroup());
            this.isShowHeadshotImage =
                this.participantImageService.showHeadshotImage(this.event.league.id, OptionGroupType.Regular) &&
                isPlayerMatched(this.event.players, this.optionGroup().options);
            if (this.isShowOptionImage) {
                this.getPlayerImages(this.optionGroup());
            }
            if (this.isShowHeadshotImage) {
                this.optionGroup().options.forEach((option) => {
                    if (option.image) {
                        this.optionImages[option.id] = option.image;
                    }
                });
            }
            if (changes.event?.previousValue !== changes.event?.currentValue) {
                this.isPlayerStatisticsEnabled =
                    this.playerStatisticsService.isPlayerStatisticsVisible(this.event.league.id, OptionGroupType.Regular) &&
                    isPlayerMatched(this.event.players, this.optionGroup().options);
                this.fetchPlayerStatistics();
            }
        }
    }

    @HostBinding('class.center-align-option') get isCorrectScore() {
        const dynamicCorrectScore_MarketType = this.optionGroup().originalParameters?.find(
            (param) => param.key === Tv2MarketParameters.MarketType && param.value === 'DynamicCorrectScore',
        );
        const points_Happening = this.optionGroup().originalParameters?.find(
            (param) => param.key === Tv2MarketParameters.Happening && param.value === 'Points',
        );

        return dynamicCorrectScore_MarketType && points_Happening;
    }

    @HostBinding('class') get borderStyle(): string {
        if (this.isUSGroupedListEnabled && this.optionGroup().options.length % 2 === 0) {
            return 'even-options';
        }

        return '';
    }

    @HostBinding('class') get participantJersey(): string {
        if (this.isShowOptionImage) {
            return 'jersey-panel';
        }

        return '';
    }

    borderClass(index: number): boolean {
        return (
            !this.isExpanded() &&
            this.threshold() > 0 &&
            this.optionGroup().options.length > this.threshold() &&
            (index === this.threshold() - 1 || index === this.threshold() - 2)
        );
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.isExpanded.set(state !== 'collapsed');
        this.publishExpandedEvent();
    }

    expand(): void {
        this.more.set(!this.more());
        this.expandClicked.emit(this.more());

        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.more() ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    private publishExpandedEvent(): void {
        this.expandClicked.emit(this.isExpanded());
        this.trackingService.track(trackingConstants.EVENT_CLICKS, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Competition Page Interactions',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Visible Selections',
            [trackingConstants.COMPONENT_ACTION_EVENT]: this.isExpanded() ? 'Show More' : 'Show Less',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
        });
    }

    trackOption(index: number, option: EventOption): number {
        return option.id;
    }

    showOption(index: number): boolean {
        return this.more() || !this.threshold() || index < this.threshold();
    }

    rendererChange(columns: number): void {
        this.threshold.set(this.calcThreshold(columns));
        this.columns.set(columns);
        if (this.useMsOptionRenderer && !this.columnsDetermined) {
            this.columnsDetermined = true;
        }
    }

    toggleStatisticsOpenState(option: EventOption): void {
        const optionGroupId = option.optionGroupId?.toString() ?? '';
        const key = `${option.optionGroupId}_${option.playerId}`;
        const isStatisticsOpen = this.isStatisticsOpen()[key];
        this.playerStatisticsService.openStatistics({
            eventId: this.event.id,
            marketFilter: this.optionGroup().originalId,
            playerName: option.name,
            marketId: optionGroupId,
            isOpen: !isStatisticsOpen,
            tracking: isStatisticsOpen ? false : undefined,
        });
    }

    isPlayer(playerName: string): boolean {
        return this.playerStatisticsService.isPlayer(this.event.players, playerName);
    }

    protected calcThreshold(columns: number) {
        if (this.optionGroup().detailedGrouping?.displayType === ExtendedDisplayType.AutomatedPriceBoost) {
            return this.optionGroup().options.length;
        }
        let threshold = this.marketGroupingConfig.showMoreRegularThreshold;
        if (this.optionsLimitOverride) {
            threshold = this.optionsLimitOverride;
        } else if (this.thresholds) {
            const active = this.thresholds
                .filter((current) => current.columns <= columns)
                .sort((first, second) => first.columns - second.columns)
                .pop();

            if (active) {
                threshold = active.threshold;
            }

            threshold = ceil(threshold / columns) * columns;
        }

        return threshold;
    }

    private getPlayerImages(optionGroup: EventOptionGroup): void {
        const options = this.participantImageService.getPlayerJersey(this.event, optionGroup);

        this.optionImages = options?.reduce(
            (acc, option) => {
                acc[option.id] = this.participantImageService.getParticipantImage(option, this.event.sport?.id, this.event.offerSource, true);

                return acc;
            },
            {} as Record<number, EventParticipantImage>,
        );
    }

    private fetchPlayerStatistics(): void {
        if (this.isPlayerStatisticsEnabled) {
            const eventId = this.event.id;
            this.store.dispatch(loadPlayerStatisticsData({ eventId, marketFilter: this.optionGroup().originalId, mainMarket: false }));
            this.subscribeToPlayerStatisticsState(eventId, this.optionGroup().originalId);
        }
    }

    private subscribeToPlayerStatisticsState(eventId: string, marketFilter: string): void {
        this.playerStatisticsSubscription?.unsubscribe();
        this.playerStatisticsSubscription = this.store
            .select(selectDataByEventAndMarket(eventId, marketFilter))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data: PlayerStatisticsStoreData) => {
                if (data) {
                    this.playerStatistics.set({
                        isLoading: data.isLoading,
                        isError: data.isError,
                        data: data.data,
                    });
                }
            });
    }
}
